import AppColors from 'general/constants/AppColors';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

ModelTreeDropdown.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  brandList: PropTypes.array,
  modelList: PropTypes.array,
};

ModelTreeDropdown.defaultProps = {
  value: '',
  brandList: [],
  modelList: [],
};

function ModelTreeDropdown({ value, onChange, brandList, modelList }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedBrands, setExpandedBrands] = useState({});
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const groupedModels = useMemo(() => _.groupBy(modelList, 'brandId'), [modelList]);

  const filteredBrands = useMemo(() => {
    if (!search) return brandList;
    return brandList.filter((brand) =>
      (groupedModels[brand.brandId] ?? []).some((m) =>
        m.name?.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [brandList, groupedModels, search]);

  const selectedModel = modelList.find((m) => m.modelId === value);

  const toggleBrand = (brandId) => {
    setExpandedBrands((prev) => ({ ...prev, [brandId]: !prev[brandId] }));
  };

  const handleSelect = (modelId) => {
    onChange(modelId);
    setIsOpen(false);
    setSearch('');
  };

  const handleToggleOpen = () => {
    setIsOpen((wasOpen) => {
      const nextOpen = !wasOpen;
      if (nextOpen) {
        if (value) {
          const model = modelList.find((m) => m.modelId === value);
          setExpandedBrands(model?.brandId ? { [model.brandId]: true } : {});
        } else {
          setExpandedBrands({});
        }
        setSearch('');
      }
      return nextOpen;
    });
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        className="btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between"
        type="button"
        onClick={handleToggleOpen}
      >
        {selectedModel?.name ?? 'Tất cả'}
      </button>
      {isOpen && (
        <div
          className="dropdown-menu show w-100 py-2"
          style={{ maxHeight: '40vh', overflowY: 'auto', zIndex: 1050, minWidth: 260 }}
        >
          <div className="mx-3 my-1 search-form-border position-sticky" style={{ top: 0, background: '#fff', zIndex: 1 }}>
            <input
              className="form-control"
              placeholder="Tìm kiếm..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div
            className="d-flex align-items-center justify-content-between dropdown-item px-5 cursor-pointer"
            onClick={() => handleSelect('')}
            style={{ paddingTop: 5, paddingBottom: 5 }}
          >
            <span style={{ fontSize: 13, fontWeight: 500, color: '#494968' }}>Tất cả</span>
            {!value && <i className="fa-regular fa-check text-success" />}
          </div>
          {filteredBrands.map((brand) => {
            const models = (groupedModels[brand.brandId] ?? []).filter(
              (m) => !search || m.name?.toLowerCase().includes(search.toLowerCase())
            );
            if (models.length === 0) return null;
            const isExpanded = search
              ? true
              : expandedBrands[brand.brandId] === true;
            return (
              <div key={brand.brandId}>
                <div
                  className="dropdown-item px-5 d-flex align-items-center cursor-pointer gap-2"
                  onClick={() => toggleBrand(brand.brandId)}
                  style={{ paddingTop: 5, paddingBottom: 5 }}
                >
                  <i
                    className={`fa-solid fa-caret-${isExpanded ? 'down' : 'right'}`}
                    style={{ fontSize: 14, color: '#494968' }}
                  />
                  <span style={{ fontSize: 13, fontWeight: 500, color: '#494968' }}>{brand.name}</span>
                </div>
                {isExpanded &&
                  models.map((model) => (
                    <div
                      key={model.modelId}
                      className="dropdown-item px-5 d-flex align-items-center justify-content-between cursor-pointer"
                      style={{
                        paddingTop: 5,
                        paddingBottom: 5,
                        background: value === model.modelId ? AppColors.gray[100] : undefined,
                      }}
                      onClick={() => handleSelect(model.modelId)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <span style={{ width: 14, flexShrink: 0 }} />
                        <i className="fa-solid fa-circle" style={{ fontSize: 6, color: '#494968' }} />
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#494968' }}>{model.name}</span>
                      </div>
                      {value === model.modelId && (
                        <i className="fa-solid fa-check text-success" />
                      )}
                    </div>
                  ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ModelTreeDropdown;
